import { useEffect, useRef } from "react";
import qrcode from "qrcode-generator";

/**
 * TexturedQr — a REAL, scannable QR rendered in the key's engraved style,
 * PROCEDURALLY (no pixel-sampling from the reference art). The look is defined
 * by shapes, not by the original's pixels:
 *
 *   parchment (tan + subtle fractal mottle)
 *     → finder patterns   drawn as unified rounded frames (clean, never dotty)
 *     → data modules      rounded ~28%, solid dark brown, softened by a hair of blur
 *     → grain             ~monochrome noise over everything
 *
 * QR geometry is taken exactly from the generated matrix (ECC 'L' so short URLs
 * stay Version 2 / 25×25 — feed it short links or the modules get tiny).
 * Scannability rests on high contrast (#1B140D on tan) + clean finders.
 */

const CELL = 28; // px per module
const QUIET = 2; // quiet-zone modules (min padding; still scans on clean bg)
const RADIUS = 0; // sharp square modules, like the reference
const BLUR = CELL * 0.02; // whisper of blur (anti-alias only) — corners stay sharp
const GRAIN = 9; // ± per-channel grain amplitude (~3.5%)
const MODULE: [number, number, number] = [27, 20, 13]; // #1B140D dark brown
const PARCH_TOP: [number, number, number] = [235, 215, 190]; // #ebd7be light
const PARCH_BOT: [number, number, number] = [178, 148, 111]; // #b2946f bronze
const PARCH_LEFT: [number, number, number] = [220, 191, 159]; // #dcbf9f left tint
const LEFT_MIX = 0.5; // how strongly the left edge pulls toward PARCH_LEFT

const rgb = (c: [number, number, number]) => `rgb(${c[0]},${c[1]},${c[2]})`;

// Parchment: a vertical gradient (light top → bronze bottom, like the key's
// metallic lighting) with one octave of smooth value-noise so it mottles like
// aged metal instead of reading as a flat swatch.
function paintParchment(ctx: CanvasRenderingContext2D, W: number, H: number) {
  const s = Math.max(3, Math.floor(CELL / 2));
  const lw = Math.ceil(W / s);
  const lh = Math.ceil(H / s);
  const low = document.createElement("canvas");
  low.width = lw;
  low.height = lh;
  const lctx = low.getContext("2d")!;
  const id = lctx.createImageData(lw, lh);
  for (let y = 0; y < lh; y++) {
    const t = y / (lh - 1); // 0 at top → 1 at bottom
    for (let x = 0; x < lw; x++) {
      const amp = 1 + (Math.random() - 0.5) * 0.08; // ±4% lightness
      const lw2 = lw > 1 ? (1 - x / (lw - 1)) * LEFT_MIX : 0; // strongest at left edge
      const i = (y * lw + x) * 4;
      for (let k = 0; k < 3; k++) {
        const vert = PARCH_TOP[k] + (PARCH_BOT[k] - PARCH_TOP[k]) * t;
        id.data[i + k] = (vert + (PARCH_LEFT[k] - vert) * lw2) * amp;
      }
      id.data[i + 3] = 255;
    }
  }
  lctx.putImageData(id, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(low, 0, 0, W, H); // smooth upscale → soft fractal blotches
}

// Monochrome grain over the finished composite.
function addGrain(ctx: CanvasRenderingContext2D, W: number, H: number) {
  const img = ctx.getImageData(0, 0, W, H);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const g = (Math.random() - 0.5) * 2 * GRAIN;
    d[i] += g;
    d[i + 1] += g;
    d[i + 2] += g;
  }
  ctx.putImageData(img, 0, 0);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

export function TexturedQr({
  data,
  matrix,
  transparent,
  reveal,
}: {
  data: string;
  matrix?: number[][];
  // modules-only: skip the parchment + grain so a real plate behind (e.g. the
  // video's blank plate) shows through around the dark modules.
  transparent?: boolean;
  // transparent mode only: when it flips true, the blocks stamp in at random
  // times (scattered) instead of all at once. Undefined = draw immediately.
  reveal?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || (!data && !matrix)) return;

    // A fixed `matrix` (e.g. the decorative replica of the video's QR) is
    // rendered verbatim — every set cell is a module, finders included. Without
    // one we generate a real, scannable QR from `data`.
    let n: number;
    let darkAt: (r: number, c: number) => boolean;
    if (matrix) {
      n = matrix.length;
      darkAt = (r, c) =>
        r >= 0 && r < n && c >= 0 && c < n && matrix[r][c] === 1;
    } else {
      const qr = qrcode(0, "L");
      qr.addData(data);
      qr.make();
      n = qr.getModuleCount();
      // The engraved look needs large modules — keep this at Version 2 (25×25).
      // A longer URL forces a higher version (tiny modules, style falls apart).
      if (import.meta.env.DEV && n > 25)
        console.warn(
          `[TexturedQr] QR is ${n}×${n} (Version ${(n - 17) / 4}). Data is too long ` +
            `for the engraved style — shorten the URL to keep it 25×25.`,
        );
      darkAt = (r, c) => r >= 0 && r < n && c >= 0 && c < n && qr.isDark(r, c);
    }
    const total = n + QUIET * 2;
    // module (mr,mc) belongs to one of the three 7×7 finder patterns
    const inFinder = (mr: number, mc: number) =>
      (mr < 7 && mc < 7) ||
      (mr < 7 && mc >= n - 7) ||
      (mr >= 7 && mc < 7 && mr >= n - 7);

    const W = total * CELL;
    const canvasEl = canvas;
    canvasEl.width = W;
    canvasEl.height = W;
    const ctx = canvasEl.getContext("2d")!;
    const off = QUIET * CELL;

    // ── transparent + scattered reveal: blocks stamp in at random times ────
    if (transparent && reveal !== undefined) {
      if (!reveal) {
        ctx.clearRect(0, 0, W, W); // nothing until it's triggered
        return;
      }
      // every dark cell (finders + data); sharp squares abut into solid rings
      const cells: { x: number; y: number; delay: number }[] = [];
      const SPREAD = 1100; // window over which all blocks appear
      const POP = 200; // per-block pop-in duration
      for (let mr = 0; mr < n; mr++)
        for (let mc = 0; mc < n; mc++)
          if (darkAt(mr, mc))
            cells.push({
              x: off + mc * CELL,
              y: off + mr * CELL,
              delay: Math.random() * SPREAD,
            });
      ctx.fillStyle = rgb(MODULE);
      let raf = 0;
      const start = performance.now();
      const frame = (now: number) => {
        const el = now - start;
        ctx.clearRect(0, 0, W, W);
        for (const c of cells) {
          const t = (el - c.delay) / POP;
          if (t <= 0) continue;
          const p = t >= 1 ? 1 : t;
          const w = CELL * (0.5 + 0.5 * p); // pop: scale up from the centre
          ctx.globalAlpha = p;
          ctx.fillRect(c.x + (CELL - w) / 2, c.y + (CELL - w) / 2, w, w);
        }
        ctx.globalAlpha = 1;
        if (el < SPREAD + POP + 40) raf = requestAnimationFrame(frame);
      };
      raf = requestAnimationFrame(frame);
      return () => cancelAnimationFrame(raf);
    }

    // ── static draw (normal page, or transparent without reveal control) ───
    // 1) parchment (skipped in transparent mode — a real plate shows through)
    if (!transparent) paintParchment(ctx, W, W);

    // 2) finder patterns — unified sharp frames. Skipped in matrix mode, where
    // the finders are already part of the sampled pattern. The ring is a donut
    // (even-odd) so the parchment shows through the gap (never erased to
    // transparent, which let the video bleed through).
    const drawFinder = (mr: number, mc: number) => {
      const x = off + mc * CELL;
      const y = off + mr * CELL;
      ctx.fillStyle = rgb(MODULE);
      ctx.beginPath();
      ctx.rect(x, y, 7 * CELL, 7 * CELL); // outer (sharp, like the reference)
      ctx.rect(x + CELL, y + CELL, 5 * CELL, 5 * CELL); // hole
      ctx.fill("evenodd"); // ring only — gap keeps the parchment underneath
      ctx.fillRect(x + 2 * CELL, y + 2 * CELL, 3 * CELL, 3 * CELL);
    };
    if (!matrix) {
      drawFinder(0, 0);
      drawFinder(0, n - 7);
      drawFinder(n - 7, 0);
    }

    // 3) modules — sharp, a whisper of blur. In matrix mode every set cell is a
    // module (finders included); otherwise the three finders are drawn above.
    ctx.filter = `blur(${BLUR}px)`;
    ctx.fillStyle = rgb(MODULE);
    for (let mr = 0; mr < n; mr++)
      for (let mc = 0; mc < n; mc++) {
        if (!darkAt(mr, mc) || (!matrix && inFinder(mr, mc))) continue;
        roundRect(ctx, off + mc * CELL, off + mr * CELL, CELL, CELL, RADIUS);
        ctx.fill();
      }
    ctx.filter = "none";

    // 4) grain over everything (skipped in transparent mode)
    if (!transparent) addGrain(ctx, W, W);
  }, [data, matrix, transparent, reveal]);

  return <canvas ref={canvasRef} className="block h-full w-full" />;
}
